import type { ReactNode } from 'react';

import { CAST_AI } from '@cast/constants';
import {
  MetricsBox,
  Panel,
  RatioProgressBar,
  tokens,
  Typography,
  Box,
  Stack,
  SquareMarker,
} from '@cast/design-system';
import {
  EMPTY_METRIC_VALUE,
  ResourceSubMetricView,
} from '@cast/features-cluster-shared-utils';
import type { ResourceMetricView } from '@cast/features-cluster-shared';

import {
  clusterDashboardResourceColors,
  dashboardTypography,
} from '../../_constants/dashboardTokens';

const MetricRow = ({
  metric,
  unit,
}: {
  metric: ResourceSubMetricView;
  unit: string;
}) => (
  <Stack key={metric.label} direction="row" alignItems="baseline">
    <SquareMarker
      color={metric.markerColor}
      sx={{ transform: 'translateY(2px)' }}
    />
    <Typography
      variant="L10R"
      pl={4}
      mr="auto"
      color={tokens.theme.fg.structural.secondary}
    >
      {metric.label}
    </Typography>
    <Typography variant="P12M">
      {metric.primaryValue}
      <Typography
        variant="P10R"
        color={tokens.theme.fg.structural.secondary}
        component="span"
        pl={2}
      >
        {unit}
      </Typography>
    </Typography>
    <Typography
      variant="P12R"
      color={tokens.theme.fg.structural.secondary}
      pl={4}
    >
      {metric.secondaryValue}
    </Typography>
  </Stack>
);

type ResourcePanelProps = {
  resource: ResourceMetricView;
  locked?: boolean;
  onInstallAgent?: () => void;
  headerEndContent?: ReactNode;
};

export const ResourcePanel = ({
  resource,
  locked = false,
  onInstallAgent,
  headerEndContent,
}: ResourcePanelProps) => (
  <Panel
    testId={`resource-panel-${resource.title.toLowerCase()}`}
    sx={{
      p: 16,
      height: '100%',
      gap: locked ? 8 : 24,
      containerType: 'inline-size',
    }}
  >
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 24,
        '@container (max-width: 500px)': {
          gridTemplateColumns: '1fr',
          gap: 12,
        },
      }}
    >
      <Stack gap={4}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={8}
        >
          <Typography variant={dashboardTypography.resourceTitle}>
            {resource.title}
          </Typography>
          {!locked && headerEndContent ? headerEndContent : null}
        </Stack>
        <MetricsBox
          size="l"
          slots={{
            value: locked ? EMPTY_METRIC_VALUE : resource.provisioned,
            unit: locked ? undefined : `${resource.unit} provisioned`,
          }}
          slotProps={{
            value: locked
              ? {
                  sx: { color: tokens.theme.disabled.fg },
                }
              : undefined,
            unit: { noWrap: true },
          }}
        />
        <Box
          sx={{
            position: 'relative',
            bgcolor: clusterDashboardResourceColors.progressTrack,
            borderRadius: '999px',
            p: 0,
          }}
        >
          <RatioProgressBar
            size="lg"
            hideBorders
            total={100}
            bars={
              !locked
                ? resource.segments.map((segment) => ({
                    value: segment.value,
                    color: segment.color,
                  }))
                : []
            }
          />
          {!locked && (
            <Box
              sx={{
                position: 'absolute',
                top: -4,
                left: `${resource.benchmarkPercent ?? 92}%`,
                width: '1px',
                height: 16,
                bgcolor: clusterDashboardResourceColors.benchmark,
              }}
            />
          )}
        </Box>
      </Stack>

      <Stack
        gap={12}
        justifyContent="space-between"
        sx={{ flexGrow: 1, minHeight: 0 }}
      >
        <Stack gap={4}>
          {!locked &&
            resource.subMetrics.map((metric) => (
              <MetricRow
                key={metric.label}
                metric={metric}
                unit={resource.unit}
              />
            ))}
        </Stack>

        {locked && onInstallAgent ? (
          <Typography
            variant="P12R"
            component="p"
            color={tokens.theme.fg.structural.primary}
          >
            <Typography
              component="button"
              type="button"
              variant="P12M"
              onClick={() => onInstallAgent()}
              color={tokens.theme.fg.action.primary}
              sx={{
                border: 0,
                background: 'none',
                p: 0,
                cursor: 'pointer',
                display: 'inline',
                verticalAlign: 'baseline',
                color: tokens.theme.fg.action.primary,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Install the {CAST_AI} agent
            </Typography>{' '}
            to view a detailed breakdown of resource usage.
          </Typography>
        ) : null}
      </Stack>
    </Box>
  </Panel>
);
